import { css } from "@emotion/react"
import { Heading } from "@planningcenter/doxy-web"
import { Icon } from "source/shared/components/Icon"
import { DesignationRow } from "./DesignationRow"
import { Designation, ValidationErrors } from "../types"
import { useDonationFormContext } from "../hooks/useDonationFormContext"
import { extractMessagesFrom } from "../stores/validations"
import { NEEDS_DESIGN_STYLES } from "source/giving/utils/dev"

type FundSelectorProps = {
  designations: Designation[]
  errors: ValidationErrors
}

export function FundSelector({ designations, errors }: FundSelectorProps) {
  const { config, dispatch } = useDonationFormContext()

  const { funds } = config
  const isSplitDonation = designations.length > 1
  const selectedFundIds = designations.map((d) => d.fundId)
  const firstDesignation = designations[0]

  const addDesignation = () => {
    dispatch({ type: "ADD_BLANK_DESIGNATION" })
  }

  const removeDesignation = (fundId: string) => {
    dispatch({ type: "REMOVE_DESIGNATION", fundId })
  }

  const handleFundChange = (fundId: string, newFundId: string) => {
    dispatch({ type: "UPDATE_DESIGNATION_FUND", fundId, newFundId })
  }

  const handleAmountChange = (fundId: string, amount: string) => {
    dispatch({ type: "UPDATE_DESIGNATION_AMOUNT", fundId, amount })
  }

  const handleBlur = () => dispatch({ type: "VALIDATE", config })

  return (
    <div>
      <Heading level={3} text="Fund" />
      <div className="mt-1">
        {isSplitDonation ? (
          <div
            className="d-f fd-c"
            css={css`
              gap: 0.75rem;
            `}
          >
            {designations.map((d, index) => {
              const availableFunds = funds.filter(
                (fund) =>
                  fund.id === d.fundId || !selectedFundIds.includes(fund.id),
              )
              const errorMessages = extractMessagesFrom(
                errors,
                `designation_${d.fundId}`,
              )
              return (
                <>
                  <DesignationRow
                    key={index}
                    index={index}
                    fundId={d.fundId}
                    amount={d.amount}
                    funds={availableFunds}
                    onFundChange={(newFundId) =>
                      handleFundChange(d.fundId, newFundId)
                    }
                    onAmountChange={(amount) =>
                      handleAmountChange(d.fundId, amount)
                    }
                    onRemove={() => removeDesignation(d.fundId)}
                    onAmountBlur={handleBlur}
                  />
                  {errorMessages.length > 0 && (
                    <p style={NEEDS_DESIGN_STYLES}>
                      {errorMessages.join(", ")}
                    </p>
                  )}
                </>
              )
            })}

            <div className="d-f jc-sb">
              <button
                className="btn badge-btn fs-1 p-0 c-tint1"
                css={css`
                  aspect-ratio: 1;
                `}
                type="button"
                onClick={addDesignation}
                aria-label="Add fund"
              >
                <Icon symbol="general#plus" aria-hidden />
              </button>
            </div>
          </div>
        ) : (
          <div className="d-f fd-c g-2">
            <label className="visually-hidden" htmlFor="designation-fund">
              Fund
            </label>
            <select
              className="select"
              id="designation-fund"
              value={firstDesignation.fundId}
              onChange={(e) =>
                handleFundChange(firstDesignation.fundId, e.target.value)
              }
            >
              {funds.map((fund) => (
                <option key={fund.id} value={fund.id}>
                  {fund.name}
                </option>
              ))}
            </select>

            <div className="d-f fd-c">
              <button
                type="button"
                className="btn text-btn compact-btn d-f ai-c mr-a c-tint1 fw-500"
                css={css`
                  border-radius: 1.5rem;
                `}
                onClick={addDesignation}
              >
                <span
                  className="btn minor-btn badge-btn p-0 mr-1 c-tint1"
                  css={css`
                    aspect-ratio: 1;
                  `}
                >
                  <Icon symbol="general#plus" aria-hidden />
                </span>{" "}
                Give to multiple funds
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
